import type { Survey } from '@engagement-web-app/common';
import { uuidSchema } from '@engagement-web-app/common/validation-utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
    createFileRoute,
    type ErrorComponentProps,
    redirect,
    retainSearchParams,
    SearchParamError,
    stripSearchParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';
import { Flex } from '@/components/Flex';
import { InvalidRequest } from '@/components/InvalidRequest';
import { NotFound, NotFoundError } from '@/components/NotFound';
import { Page } from '@/components/Page';
import { Pagination } from '@/components/Pagination';
import { RouteError } from '@/components/RouteError';
import { Skeleton } from '@/components/Skeleton';
import { SurveyCommentsTable, SurveyCommentsTableSkeleton } from '@/components/surveys/comments/SurveyCommentsTable';
import { CreatePdfButton } from '@/components/surveys/common/CreatePdfButton';
import { SelectSurvey } from '@/components/surveys/common/SelectSurvey';
import { useTopRef } from '@/hooks';
import { useSurveyPermission } from '@/permissions/SurveyPermissionProvider';
import { fetchSurveyComments, fetchSurveys } from '@/queries/surveys/client';
import { combineQueryErrors, debounce, formatDateToLabel } from '@/utils';
import { useCommentTags, useSurveyCommentQuestions } from '../-hooks';

const DefaultRouteQueryParams = {
    page: 1,
    surveyId: '',
};

const RouteQueryParams = z.object({
    page: z.coerce.number().int().min(1).default(DefaultRouteQueryParams.page),
    surveyId: uuidSchema.default(DefaultRouteQueryParams.surveyId),
});

export const Route = createFileRoute('/_client/_auth/surveys/comments/')({
    component: SurveyCommentsRoute,
    validateSearch: zodValidator(RouteQueryParams),
    search: {
        middlewares: [retainSearchParams(['surveyId']), stripSearchParams(DefaultRouteQueryParams)],
    },
    loaderDeps: ({ search }) => ({ surveyId: search.surveyId }),
    loader: async ({ deps }) => {
        const { surveys } = await fetchSurveys();

        if (deps.surveyId === '' && surveys.length > 0) {
            // Canonicalizes the URL to the survey actually being shown, so a shared/reloaded
            // link keeps pointing at it rather than silently drifting to whatever's newest.
            throw redirect({ to: '/surveys/comments', search: { surveyId: surveys[0].surveyId }, replace: true });
        }

        if (!surveys.some((survey) => survey.surveyId === deps.surveyId)) {
            throw new NotFoundError();
        }

        return { surveys };
    },
    pendingComponent: SurveyCommentsRouteSkeleton,
    errorComponent: SurveyCommentsRouteError,
});

/**
 * The loader only confirms `surveyId` refers to a real survey — it can't check
 * comment-viewing permission, since `useSurveyPermission` requires the layout's
 * provider to be mounted here first. This narrows `surveys` down to the ones the
 * user may view comments on, and fails outright if `surveyId` isn't one of them,
 * rather than silently substituting a different survey than the URL points at.
 */
function SurveyCommentsRoute() {
    const { surveys } = Route.useLoaderData();
    const { surveyId } = Route.useSearch();
    const { canViewComments } = useSurveyPermission();

    // Comments can't be narrowed by attribute, so a survey is either readable
    // here in full or not offered at all.
    const commentViewableSurveys = useMemo(
        () => surveys.filter((survey) => canViewComments(survey.surveyId)),
        [surveys, canViewComments]
    );

    if (commentViewableSurveys.length === 0) {
        // TODO: Visual design for empty state
        return <div>サーベイがありません。</div>;
    }

    if (!canViewComments(surveyId)) {
        return (
            <RouteError
                kind="client"
                title="結果分析 - コメント"
                message="このサーベイのコメントを閲覧する権限がありません。"
                backTo={{ to: '/surveys/comments', label: '最新のサーベイに戻る' }}
            />
        );
    }

    return <Content surveys={commentViewableSurveys} surveyId={surveyId} />;
}

type ContentProps = {
    surveys: Survey[];
    surveyId: string;
};

/** Renders the actual page — `surveys` is already narrowed to ones the user may view comments on. */
function Content({ surveys, surveyId }: ContentProps) {
    const navigate = Route.useNavigate();

    return (
        <Page.Root>
            <Page.Header title="結果分析 - コメント" />

            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    <SelectSurvey
                        surveys={surveys.map((survey) => ({
                            id: survey.surveyId,
                            name: formatDateToLabel(survey.surveyDate),
                        }))}
                        selectedSurveyId={surveyId}
                        onSurveyChange={(id) => navigate({ search: (prev) => ({ ...prev, surveyId: id, page: 1 }) })}
                    />

                    <CreatePdfButton />
                </Flex>

                {/* Using `key` re-mounts the component when `surveyId` changes to reset the internal state. */}
                <SurveyComments key={surveyId} selectedSurveyId={surveyId} />
            </Page.Content>
        </Page.Root>
    );
}

function SurveyCommentsRouteSkeleton() {
    return (
        <Page.Root>
            {/* The title is static, so it renders for real rather than as a placeholder. */}
            <Page.Header title="結果分析 - コメント" />

            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    {/* Absolute widths: these sit in shrink-to-fit flex items, so a
                        percentage would have no definite width to resolve against. */}
                    <Flex direction="column" gap="2">
                        <Skeleton.Line width={96} height="sm" />
                        <Skeleton.Box width={300} height={42} />
                    </Flex>

                    <Skeleton.Box width={168} height={36} />
                </Flex>

                <SurveyCommentsTableSkeleton />
            </Page.Content>
        </Page.Root>
    );
}

function SurveyCommentsRouteError({ error, reset }: ErrorComponentProps) {
    if (error instanceof SearchParamError) {
        return <InvalidRequest kind="client" />;
    }

    if (error instanceof NotFoundError) {
        return <NotFound kind="client" />;
    }

    return (
        <RouteError
            kind="client"
            title="結果分析 - コメント"
            onRetry={reset}
            message="結果分析 - コメントの取得に失敗しました。"
        />
    );
}
const COMMENTS_PAGE_SIZE = 50;

type SurveyCommentsProps = {
    selectedSurveyId: string;
};

function SurveyComments({ selectedSurveyId }: SurveyCommentsProps) {
    const [selectedQuestionId, setSelectedQuestionId] = useState<string>('');
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
    // Two search states: `searchText` mirrors the input value for a responsive
    // UI, `debouncedSearchText` is the value that drives the actual query so we
    // don't re-fetch on every keystroke.
    const [searchText, setSearchText] = useState<string>('');
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');

    const navigate = Route.useNavigate();
    const { page } = Route.useSearch();

    const { topRef, TopRef } = useTopRef();

    const questionsQuery = useSurveyCommentQuestions(selectedSurveyId);
    const tagCatalogueQuery = useCommentTags(selectedSurveyId);

    const goToPage = useCallback(
        (nextPage: number) => {
            navigate({ search: (prev) => ({ ...prev, page: nextPage }) });
        },
        [navigate]
    );

    const debouncedTextSearch = useCallback(
        debounce((text: string) => {
            setDebouncedSearchText(text);
            goToPage(1);
        }, 300),
        []
    );

    const commentsQuery = useQuery({
        queryKey: ['surveyComments', selectedSurveyId, selectedQuestionId, selectedTagIds, debouncedSearchText, page],
        queryFn: () =>
            fetchSurveyComments(
                { surveyId: selectedSurveyId },
                {
                    questionIds: selectedQuestionId ? [selectedQuestionId] : undefined,
                    tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
                    search: debouncedSearchText || undefined,
                    page,
                    pageSize: COMMENTS_PAGE_SIZE,
                }
            ),
        placeholderData: keepPreviousData,
    });

    const { isError, retry } = combineQueryErrors(questionsQuery, tagCatalogueQuery, commentsQuery);

    const comments = commentsQuery.data?.comments ?? null;
    const lastPage = Math.ceil((commentsQuery.data?.total ?? 0) / COMMENTS_PAGE_SIZE);

    const handlePageChange = (nextPage: number) => {
        goToPage(nextPage);
        topRef.current?.scrollIntoView({ block: 'start' });
    };

    return (
        <>
            <TopRef />

            <SurveyCommentsTable
                comments={comments}
                questions={questionsQuery.data ?? null}
                selectedQuestionId={selectedQuestionId}
                onSelectedQuestionIdChange={(id) => {
                    setSelectedQuestionId(id);
                    goToPage(1);
                }}
                tagCatalogue={tagCatalogueQuery.data ?? null}
                selectedTagIds={selectedTagIds}
                onSelectedTagIdsChange={(ids) => {
                    setSelectedTagIds(ids);
                    goToPage(1);
                }}
                searchText={searchText}
                onSearchTextChange={(text) => {
                    setSearchText(text);
                    debouncedTextSearch(text);
                }}
                isFetching={commentsQuery.isFetching}
                isError={isError}
                onRetry={retry}
            />

            {!isError && (
                <Flex justify="center">
                    <Pagination currentPage={page} lastPage={lastPage} onPageChange={handlePageChange} />
                </Flex>
            )}
        </>
    );
}
